/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type {
  BasePowerConditioningSystem,
  ListResultsQuery,
  PCSListEntry,
  PCSRecord,
  PowerConditioningSystem,
  QueryComponentsSearchParams,
} from '@poweralpha/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { HTTPError, ResponsePromise } from 'ky';
import { api, getQueryComponentsSearchParams } from '../../api';
import { objectToQueryKey } from '../../queryClient';
import type { QueryOptions } from '../../react-components/queries';
import type { PCSTypes } from './models';

export interface PcsQueryReturn {
  inverter?: PCSRecord<PowerConditioningSystem>;
  mvTransformer?: PCSRecord<BasePowerConditioningSystem>;
}

export const createPCS = async (
  data: BasePowerConditioningSystem,
  pcsType: PCSTypes,
): Promise<ResponsePromise> => api.post(`ess/pcs/${pcsType}`, { json: data }).json();

const getPCS = async <T extends BasePowerConditioningSystem = BasePowerConditioningSystem>(
  id: string | undefined,
  type: PCSTypes,
): Promise<PCSRecord<T>> => api.get(`ess/pcs/${type}/${id}`).json();

export const usePcsQuery = <T extends BasePowerConditioningSystem = BasePowerConditioningSystem>(
  id: string | undefined,
  type: PCSTypes,
) =>
  useQuery<PCSRecord<T> | null>({
    queryKey: ['pcs', id],
    queryFn: () => getPCS(id, type),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });

export const getPCSs = async <
  T extends BasePowerConditioningSystem,
  ListOnly extends boolean = false,
>(
  pcsType: PCSTypes,
  opts?: QueryComponentsSearchParams<ListOnly>,
): Promise<ListResultsQuery<ListOnly extends true ? PCSListEntry : PCSRecord<T>>> => {
  const searchParams = getQueryComponentsSearchParams(opts);
  return api.get(`ess/pcs/${pcsType}`, { searchParams }).json();
};

export const archivePCS = async (id: string | undefined): Promise<void> =>
  api.post(`ess/pcs/${id}/archive`).json();

export const useUpdatePCSComment = (id: string | null, onSuccess: () => void) =>
  useMutation<void, HTTPError, string>({
    mutationFn: (comment) => api.post(`ess/pcs/${id}/comment`, { json: { comment } }).json(),
    onSuccess,
  });

export const getMultiplePCSs = async (
  types: Partial<Record<keyof PcsQueryReturn, string | undefined>>,
): Promise<PcsQueryReturn> => {
  let inv = {};
  let conv = {};
  if (types.inverter) {
    inv = { inverter: await getPCS(types.inverter, 'inverter') };
  }
  if (types.mvTransformer) {
    conv = { mvTransformer: await getPCS(types.mvTransformer, 'mv-transformer') };
  }

  return { ...inv, ...conv };
};

export const useInvertersQuery = <T = ListResultsQuery<PCSRecord<PowerConditioningSystem>>>(
  opts?: QueryOptions<ListResultsQuery<PCSRecord<PowerConditioningSystem>>, HTTPError, T> & {
    searchParams?: QueryComponentsSearchParams;
  },
) =>
  useQuery({
    queryKey: ['inverters', ...objectToQueryKey(opts?.searchParams)],
    queryFn: () => getPCSs<PowerConditioningSystem>('inverter', opts?.searchParams),
    ...opts,
  });

export const useMvtsQuery = <T = ListResultsQuery<PCSRecord<BasePowerConditioningSystem>>>(
  opts?: QueryOptions<ListResultsQuery<PCSRecord<BasePowerConditioningSystem>>, HTTPError, T> & {
    searchParams?: QueryComponentsSearchParams;
  },
) =>
  useQuery({
    queryKey: ['mv-transformers', ...objectToQueryKey(opts?.searchParams)],
    queryFn: () => getPCSs('mv-transformer', opts?.searchParams),
    ...opts,
  });
